"use client"

import React, { useRef, useState, useEffect, useCallback } from 'react'
import { X, Maximize2, Minimize2, Volume2, VolumeX, Play, Pause, Trash2, Download, Keyboard, Mic } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Photo, deletePhoto } from '@/features/photos/api/photos.api'
import { downloadPhotoOrVideo } from '../lib/download-media'

interface VideoPlayerProps {
    photo: Photo
    isOpen: boolean
    onClose: () => void
    onDelete?: () => void // Callback after successful deletion
    canDelete?: boolean // Whether user can delete (defaults to true)
    /** When true, show Download button (Admin/Base Leader only). */
    canDownload?: boolean
    /** Same-origin API path for direct download (e.g. /api/base-photos/123/download). When set with canDownload, triggers file download instead of opening in new tab. */
    downloadApiPath?: string
    /** Optional custom delete function (e.g. for base photos); when provided, used instead of deletePhoto */
    deletePhotoFn?: (id: number) => Promise<{ success: boolean; message?: string }>
    /** Optional: show Add description buttons (Voice/Keyboard); when provided, onEditDescriptionVoice and onEditDescriptionText are called */
    onEditDescriptionVoice?: () => void
    onEditDescriptionText?: () => void
}

/**
 * Format duration in seconds to MM:SS or HH:MM:SS format
 */
function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * VideoPlayer component - Full-screen modal video player
 * 
 * Features:
 * - Play/pause controls
 * - Progress bar
 * - Volume control
 * - Fullscreen toggle
 * - Keyboard shortcuts (spacebar to play/pause, escape to close)
 */
export default function VideoPlayer({ photo, isOpen, onClose, onDelete, canDelete = true, canDownload = false, downloadApiPath, deletePhotoFn, onEditDescriptionVoice, onEditDescriptionText }: VideoPlayerProps) {
    const [isDeleting, setIsDeleting] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [volume, setVolume] = useState(1)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showControls, setShowControls] = useState(true)
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Reset state when video changes
    useEffect(() => {
        if (!isOpen) {
            setCurrentTime(0)
            setIsPlaying(false)
            setPlaybackError(null)
            if (videoRef.current) {
                videoRef.current.currentTime = 0
            }
        }
    }, [isOpen, photo.id])

    // Autoplay muted when modal opens (browsers typically allow muted autoplay)
    useEffect(() => {
        if (!isOpen || !photo?.imageUrl) return

        const video = videoRef.current
        if (!video) return

        const attemptAutoplay = () => {
            video.muted = true
            setIsMuted(true)
            video.play().then(() => {
                setIsPlaying(true)
            }).catch(() => {
                // Autoplay blocked (e.g. Safari, strict policies) – user clicks to play
            })
        }

        if (video.readyState >= 2) {
            attemptAutoplay()
        } else {
            video.addEventListener('loadeddata', attemptAutoplay, { once: true })
            return () => video.removeEventListener('loadeddata', attemptAutoplay)
        }
    }, [isOpen, photo?.id, photo?.imageUrl])

    const [playbackError, setPlaybackError] = useState<string | null>(null)

    // Update current time as video plays
    useEffect(() => {
        const video = videoRef.current
        if (!video) return

        const updateTime = () => setCurrentTime(video.currentTime)
        const updateDuration = () => setDuration(video.duration)
        const handlePlay = () => {
            setIsPlaying(true)
            setPlaybackError(null)
        }
        const handlePause = () => setIsPlaying(false)
        const handleEnded = () => setIsPlaying(false)
        const handleError = () => {
            setIsPlaying(false)
            const error = video.error
            if (error) {
                let errorMessage = 'Unable to play video'
                switch (error.code) {
                    case MediaError.MEDIA_ERR_ABORTED:
                        errorMessage = 'Video playback was aborted'
                        break
                    case MediaError.MEDIA_ERR_NETWORK:
                        errorMessage = 'Network error while loading video'
                        break
                    case MediaError.MEDIA_ERR_DECODE:
                        errorMessage = 'Video format not supported by browser. The video may need to be converted.'
                        break
                    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                        errorMessage = 'Video format not supported. Please contact support if this issue persists.'
                        break
                }
                setPlaybackError(errorMessage)
                console.error('Video playback error:', errorMessage, error)
            }
        }

        video.addEventListener('timeupdate', updateTime)
        video.addEventListener('loadedmetadata', updateDuration)
        video.addEventListener('play', handlePlay)
        video.addEventListener('pause', handlePause)
        video.addEventListener('ended', handleEnded)
        video.addEventListener('error', handleError)

        return () => {
            video.removeEventListener('timeupdate', updateTime)
            video.removeEventListener('loadedmetadata', updateDuration)
            video.removeEventListener('play', handlePlay)
            video.removeEventListener('pause', handlePause)
            video.removeEventListener('ended', handleEnded)
            video.removeEventListener('error', handleError)
        }
    }, [isOpen])

    // Handle fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement)
        }

        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
        }
    }, [])

    const togglePlayPause = useCallback(() => {
        const video = videoRef.current
        if (!video) return

        if (isPlaying) {
            video.pause()
        } else {
            video.play()
        }
    }, [isPlaying])

    const toggleMute = useCallback(() => {
        const video = videoRef.current
        if (!video) return

        const newMuted = !isMuted
        video.muted = newMuted
        setIsMuted(newMuted)
    }, [isMuted])

    const toggleFullscreen = useCallback(async () => {
        const container = containerRef.current
        if (!container) return

        try {
            if (isFullscreen) {
                await document.exitFullscreen()
            } else {
                await container.requestFullscreen()
            }
        } catch (error) {
            console.error('Error toggling fullscreen:', error)
        }
    }, [isFullscreen])

    const exitFullscreen = useCallback(async () => {
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen()
            }
        } catch (error) {
            console.error('Error exiting fullscreen:', error)
        }
    }, [])

    // Keyboard shortcuts
    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (e: KeyboardEvent) => {
            const video = videoRef.current
            if (!video) return

            switch (e.key) {
                case ' ':
                    e.preventDefault()
                    togglePlayPause()
                    break
                case 'Escape':
                    if (isFullscreen) {
                        exitFullscreen()
                    } else {
                        onClose()
                    }
                    break
                case 'ArrowLeft':
                    e.preventDefault()
                    video.currentTime = Math.max(0, video.currentTime - 10)
                    break
                case 'ArrowRight':
                    e.preventDefault()
                    video.currentTime = Math.min(duration, video.currentTime + 10)
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    setVolume(Math.min(1, volume + 0.1))
                    break
                case 'ArrowDown':
                    e.preventDefault()
                    setVolume(Math.max(0, volume - 0.1))
                    break
                case 'm':
                case 'M':
                    toggleMute()
                    break
                case 'f':
                case 'F':
                    toggleFullscreen()
                    break
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, isFullscreen, volume, duration, onClose, togglePlayPause, toggleMute, toggleFullscreen, exitFullscreen])

    // Auto-hide controls
    useEffect(() => {
        if (!isOpen) return

        const resetControlsTimeout = () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current)
            }
            
            setShowControls(true)
            
            if (isPlaying) {
                controlsTimeoutRef.current = setTimeout(() => {
                    setShowControls(false)
                }, 3000)
            }
        }

        resetControlsTimeout()

        const video = videoRef.current
        if (video) {
            video.addEventListener('mousemove', resetControlsTimeout)
            video.addEventListener('click', resetControlsTimeout)
        }

        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current)
            }
            if (video) {
                video.removeEventListener('mousemove', resetControlsTimeout)
                video.removeEventListener('click', resetControlsTimeout)
            }
        }
    }, [isOpen, isPlaying])

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const video = videoRef.current
        if (!video) return

        const newTime = parseFloat(e.target.value)
        video.currentTime = newTime
        setCurrentTime(newTime)
    }

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const video = videoRef.current
        if (!video) return

        const newVolume = parseFloat(e.target.value)
        video.volume = newVolume
        setVolume(newVolume)
        setIsMuted(newVolume === 0)
    }

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const video = videoRef.current
        if (!video) return

        const progressBar = e.currentTarget
        const rect = progressBar.getBoundingClientRect()
        const percent = (e.clientX - rect.left) / rect.width
        const newTime = percent * duration
        video.currentTime = newTime
        setCurrentTime(newTime)
    }

    const handleDelete = async () => {
        if (!canDelete) return
        
        const confirmed = window.confirm('Are you sure you want to delete this video? This action cannot be undone.')
        if (!confirmed) return

        setIsDeleting(true)
        try {
            const deleteFn = deletePhotoFn ?? deletePhoto
            const result = await deleteFn(photo.id)
            
            if (result.success) {
                // Close the video player
                onClose()
                // Call the onDelete callback if provided (to refresh the list)
                if (onDelete) {
                    onDelete()
                }
            } else {
                alert(`Failed to delete video: ${result.message || 'Unknown error'}`)
                setIsDeleting(false)
            }
        } catch (error) {
            console.error('Error deleting video:', error)
            alert('An error occurred while deleting the video')
            setIsDeleting(false)
        }
    }

    if (!isOpen) return null

    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-95">

            <div
                ref={containerRef}
                className="relative w-full h-full flex flex-col"
                onMouseMove={() => {
                    setShowControls(true)
                    if (controlsTimeoutRef.current) {
                        clearTimeout(controlsTimeoutRef.current)
                    }
                    if (isPlaying) {
                        controlsTimeoutRef.current = setTimeout(() => {
                            setShowControls(false)
                        }, 3000)
                    }
                }}
            >
                {/* Top bar: description buttons (when present) then Download, Delete, X */}
                <div className="absolute top-4 right-4 z-20 flex items-center gap-3">
                    {(onEditDescriptionVoice || onEditDescriptionText) && (
                        <div className="flex items-center gap-1">
                            {onEditDescriptionVoice && (
                                <Button
                                    variant="secondary"
                                    size="icon"
                                    className="bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full"
                                    onClick={onEditDescriptionVoice}
                                    title="Add description (voice)"
                                >
                                    <Mic className="w-5 h-5" />
                                </Button>
                            )}
                            {onEditDescriptionText && (
                                <Button
                                    variant="secondary"
                                    size="icon"
                                    className="bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full"
                                    onClick={onEditDescriptionText}
                                    title="Add description (keyboard)"
                                >
                                    <Keyboard className="w-5 h-5" />
                                </Button>
                            )}
                        </div>
                    )}
                    {canDownload && (
                        <Button
                            variant="default"
                            size="icon"
                            className="rounded-full"
                            onClick={() => downloadPhotoOrVideo(photo.imageUrl, { id: photo.id, mediaType: 'video', downloadApiPath })}
                            title="Download video"
                            aria-label="Download video"
                        >
                            <Download className="w-5 h-5" />
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            variant="destructive"
                            size="icon"
                            className="rounded-full"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            title="Delete video"
                        >
                            {isDeleting ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Trash2 className="w-5 h-5" />
                            )}
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full"
                        onClick={onClose}
                        disabled={isDeleting}
                        aria-label="Close"
                    >
                        <X className="w-6 h-6" />
                    </Button>
                </div>

                {/* Video Container */}
                <div className="flex-1 flex items-center justify-center p-4 relative">
                    {playbackError ? (
                        <div className="text-center text-white p-8 max-w-md">
                            <div className="text-6xl mb-4">⚠️</div>
                            <h3 className="text-xl font-semibold mb-2">Video Playback Error</h3>
                            <p className="text-white/80 mb-4">{playbackError}</p>
                            <p className="text-sm text-white/60">
                                If this problem persists, the video may need to be re-uploaded or converted to MP4 format.
                            </p>
                        </div>
                    ) : (
                        <>
                            <video
                                ref={videoRef}
                                src={photo.imageUrl}
                                className="max-w-full max-h-full"
                                onClick={togglePlayPause}
                                onMouseEnter={() => setShowControls(true)}
                            />
                            {/* Centered play overlay when paused – clear "click to play" affordance */}
                            {!isPlaying && (
                                <div
                                    className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
                                    onClick={togglePlayPause}
                                    aria-hidden="true"
                                >
                                    <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:bg-white transition-colors">
                                        <Play className="w-10 h-10 text-black fill-black ml-1" />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Controls */}
                <div
                    className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 transition-opacity duration-300 ${
                        showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                >
                    {/* Progress Bar */}
                    <div
                        className="relative h-2 bg-white/20 rounded-full mb-4 cursor-pointer group"
                        onClick={handleProgressClick}
                    >
                        <div
                            className="absolute left-0 top-0 h-full bg-white rounded-full transition-all"
                            style={{ width: `${progressPercent}%` }}
                        />
                        <div
                            className="absolute h-4 w-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity -top-1"
                            style={{ left: `calc(${progressPercent}% - 8px)` }}
                        />
                    </div>

                    {/* Controls Row */}
                    <div className="flex items-center gap-4">
                        {/* Play/Pause */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-white hover:bg-white/20"
                            onClick={togglePlayPause}
                        >
                            {isPlaying ? (
                                <Pause className="w-6 h-6" />
                            ) : (
                                <Play className="w-6 h-6" />
                            )}
                        </Button>

                        {/* Time Display */}
                        <div className="text-white text-sm font-mono">
                            {formatDuration(currentTime)} / {formatDuration(duration || photo.duration || 0)}
                        </div>

                        {/* Volume Control */}
                        <div className="flex items-center gap-2 flex-1 max-w-[150px]">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-white hover:bg-white/20"
                                onClick={toggleMute}
                            >
                                {isMuted || volume === 0 ? (
                                    <VolumeX className="w-5 h-5" />
                                ) : (
                                    <Volume2 className="w-5 h-5" />
                                )}
                            </Button>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={isMuted ? 0 : volume}
                                onChange={handleVolumeChange}
                                className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>

                        {/* Fullscreen */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-white hover:bg-white/20 ml-auto"
                            onClick={toggleFullscreen}
                        >
                            {isFullscreen ? (
                                <Minimize2 className="w-5 h-5" />
                            ) : (
                                <Maximize2 className="w-5 h-5" />
                            )}
                        </Button>
                    </div>

                    {/* Video Info */}
                    {photo.description && (
                        <div className="text-white text-sm mt-2 opacity-75">
                            {photo.description}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

